import React, { useMemo, useState } from 'react';
import { Search, ChevronDown, Folder, MoreHorizontal, Plus, List, LayoutGrid, Network } from 'lucide-react';
import { TRACKS, GROUP_NAMES, TrackStatus } from '../../data/workspaceTracks';

// ── Tracks ───────────────────────────────────────────────────────────────────
// A flat, filterable library of tracks. Each card shows the track's name and the
// group that owns it, plus its type. Filters: group, status, type, sort, layout.
// ─────────────────────────────────────────────────────────────────────────────

const STATUSES: {
  value: TrackStatus;
  label: string;
}[] = [{
  value: 'active',
  label: 'Active'
}, {
  value: 'dormant',
  label: 'Dormant'
}, {
  value: 'archived',
  label: 'Archived'
}];
const TYPES = ['All track types', 'area', 'project', 'exploration', 'product / skills backlog'];
export function TracksView() {
  const [group, setGroup] = useState('Group');
  const [status, setStatus] = useState<TrackStatus>('active');
  const [type, setType] = useState(TYPES[0]);
  const [sort, setSort] = useState<'Name' | 'Group'>('Name');
  const [layout, setLayout] = useState<'list' | 'grid' | 'tree'>('grid');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const rows = TRACKS.filter(t => t.status === status).filter(t => group === 'Group' || t.group === group).filter(t => type === TYPES[0] || t.type === type).filter(t => t.name.toLowerCase().includes(query.trim().toLowerCase()));
    return [...rows].sort((a, b) => sort === 'Name' ? a.name.localeCompare(b.name) : a.group.localeCompare(b.group) || a.name.localeCompare(b.name));
  }, [group, status, type, sort, query]);
  return <div className="p-6 md:p-8" data-id="element-2283" data-mp="2avqr275s3b6m">
      {/* Header + filters */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-2284" data-mp="2evr5vswshoe7">
        <h1 className="mr-auto text-[26px] font-bold text-neutral-900" data-id="element-2285" data-mp="92apwgc2edxe">
          Tracks
        </h1>

        <Dropdown value={group} onChange={setGroup} options={['Group', ...GROUP_NAMES]} label="Filter by group" data-id="element-2286" data-mp="d44lwbbbetr8" />

        <Dropdown value={STATUSES.find(s => s.value === status)!.label} onChange={v => setStatus(STATUSES.find(s => s.label === v)!.value)} options={STATUSES.map(s => s.label)} label="Filter by status" dot data-id="element-2287" data-mp="1m0tajwnhn86z" />

        <Dropdown value={type} onChange={setType} options={TYPES} label="Filter by track type" data-id="element-2288" data-mp="1d0wgtgm8rj52" />

        <Dropdown value={sort} onChange={v => setSort(v as 'Name' | 'Group')} options={['Name', 'Group']} label="Sort by" prefix="Sort" data-id="element-2289" data-mp="181tn2sbpozce" />

        <div className="flex items-center gap-0.5 rounded-lg border border-neutral-200 bg-white p-0.5" data-id="element-2290" data-mp="24t5a6bmrbvdf">
          {([['list', List], ['grid', LayoutGrid], ['tree', Network]] as const).map(([key, Icon]) => <button key={key} type="button" onClick={() => setLayout(key)} aria-pressed={layout === key} aria-label={`${key} layout`} className={`rounded-md p-1.5 transition-colors ${layout === key ? 'bg-neutral-900 text-white' : 'text-neutral-400 hover:text-neutral-700'}`} data-id="element-2291" data-mp="1vwmlxcph0vn6">
              <Icon size={15} data-id="element-2292" data-mp="2jmqp5iyqytdn" />
            </button>)}
        </div>

        <button type="button" aria-label="New track" className="rounded-lg bg-neutral-900 p-2 text-white transition-colors hover:bg-neutral-800" data-id="element-2293" data-mp="2qzzrcc332hgq">
          <Plus size={16} data-id="element-2294" data-mp="2vyt2hxuwdwdq" />
        </button>
      </div>

      {/* Search */}
      <div className="relative mt-5" data-id="element-2295" data-mp="30wun4n7z23rb">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-2296" data-mp="31009vvt4otjv" />
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${TRACKS.length} tracks`} aria-label="Search tracks" className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-10 pr-4 text-[14px] text-neutral-900 outline-none transition-shadow placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-900/10" data-id="element-2297" data-mp="34wweo3jztgsv" />
      </div>

      {/* Tracks */}
      {visible.length === 0 ? <p className="mt-10 text-center text-[14px] text-neutral-500" data-id="element-2298" data-mp="3spv3w5rxl7ev">
          No tracks match these filters.
        </p> : <ul className={`mt-5 gap-3 ${layout === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'flex flex-col'}`} data-id="element-2299" data-mp="1j23v58vjebx">
          {visible.map(t => <li key={t.name} data-id="element-2300" data-mp="3fzubr7ebljz7">
              <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3 transition-colors hover:border-neutral-300" data-id="element-2301" data-mp="3fyj7l6gyqa7k">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-500" data-id="element-2302" data-mp="3orwan18qqs7d">
                  <Folder size={16} data-id="element-2303" data-mp="311jwv9qte18v" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-2304" data-mp="3144xxwjagd5k">
                  <span className="block truncate text-[14.5px] font-semibold text-neutral-900" data-id="element-2305" data-mp="2x1rs784jkf8s">
                    {t.name}
                  </span>
                  <span className="block truncate text-[12.5px] text-neutral-500" data-id="element-2306" data-mp="361elt7r14gra">
                    {t.group} · {t.type}
                  </span>
                </span>
                <span aria-label={t.status} title={t.status} className={`h-2 w-2 shrink-0 rounded-full ${t.status === 'active' ? 'bg-emerald-500' : t.status === 'dormant' ? 'bg-amber-400' : 'bg-neutral-300'}`} data-id="element-2307" data-mp="q8fntk9yful2" />
                <button type="button" aria-label={`Options for ${t.name}`} className="shrink-0 rounded-md p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700" data-id="element-2308" data-mp="23ax1l91l024t">
                  <MoreHorizontal size={16} data-id="element-2309" data-mp="1u9ywenfepokn" />
                </button>
              </div>
            </li>)}
        </ul>}
    </div>;
}
function Dropdown({
  value,
  onChange,
  options,
  label,
  dot = false,
  prefix
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  label: string;
  dot?: boolean;
  prefix?: string;
}) {
  return <div className="relative" data-id="element-2310" data-mp="2d7rwlhynbkec">
      <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} className="appearance-none rounded-lg border border-neutral-200 bg-white py-2 pl-3 pr-9 text-[13.5px] font-medium text-neutral-700 outline-none transition-colors hover:border-neutral-300 focus:border-neutral-400" data-id="element-2311" data-mp="2d6gs1fxdm8m6">
        {options.map(o => <option key={o} value={o} data-id="element-2312" data-mp="3aogwh641ccl3">
            {prefix ? `${prefix} · ${o}` : o}
          </option>)}
      </select>
      {dot && <span className="pointer-events-none absolute right-7 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-emerald-500" data-id="element-2313" data-mp="3fjj92ecl728w" />}
      <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-2314" data-mp="3aj5bjq8m3ap4" />
    </div>;
}